import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cx, ui, focus } from '../../lib/ui-primitives'
import { BackIcon } from '../icons'

// ============================================
// BACK BUTTON
// Navigation back button with proper touch target
// ============================================

export interface BackButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Show text label (default: true) */
  showLabel?: boolean
  /** Custom label text */
  label?: string
}

/**
 * BackButton - Navigation back button
 *
 * Extracted from: 6 screens (NetworkScreen, AmountScreen, DetailsScreen,
 * PaymentScreen, ConfirmScreen, LookupScreen)
 *
 * Features:
 * - 44x44px minimum touch target
 * - Accessible with aria-label
 * - Optional text label
 * - Consistent hover/focus states
 */
export const BackButton = forwardRef<HTMLButtonElement, BackButtonProps>(
  (
    {
      showLabel = true,
      label = 'Back',
      className,
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        className={cx(
          ui.backBtn,
          focus.ring,
          className
        )}
        aria-label={ariaLabel || `Go back${showLabel ? '' : ` - ${label}`}`}
        {...props}
      >
        <BackIcon aria-hidden="true" />
        {showLabel && <span>{label}</span>}
      </button>
    )
  }
)

BackButton.displayName = 'BackButton'
